<template>
  <div class="sp-table-per-page" part="per-page">
    <span class="sp-table-per-page__label">{{ label }}</span>
    <sp-select
      class="sp-table-per-page__select"
      appearance="inline"
      density="compact"
      :aria-label="label"
      :items.prop="items"
      :value.attr="selected"
      @input="onInput"
    ></sp-select>
  </div>
</template>

<script setup>
import { computed } from "vue";

import { t } from "#ce/shared/components-i18n";

const props = defineProps({
  perPage: {
    type: Number,
    default: undefined,
  },
  /**
   * Page sizes to offer, which always include the current one.
   */
  options: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["change"]);

const label = computed(() => t("table_per_page"));

// sp-select matches its selection against the item values, and a value bound through an attribute
// arrives as a string, so the sizes cross as strings and convert back on the way out.
const items = computed(() => props.options.map(String));
const selected = computed(() => (props.perPage === undefined ? undefined : String(props.perPage)));

// A registered element's event carries the emit arguments as an array, so the size is the first of
// them; coercing the array itself only works while there is exactly one.
function onInput(event) {
  const [value] = event.detail;

  emit("change", Number(value));
}
</script>

<style lang="scss" scoped>
.sp-table-per-page {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-ref-spacing-2);

  // The picker sits in a row of x-small buttons, so it matches their height rather than a form
  // field's. The host is a block and would otherwise fill the pager, hence the width.
  &__select {
    --sp-comp-text-field-min-width: 0;
    --sp-comp-text-field-font-size: 1em;
    --sp-comp-caret-size: 1.25em;
    // The caret is box-centred here, so both of its optical nudges for inline use come off.
    --sp-comp-caret-offset: 0;
    --sp-comp-caret-active-nudge: 0;
    --sp-comp-text-field-compact-min-height: var(--sp-comp-table-per-page-height, 1.5rem);
    --sp-comp-text-field-compact-padding: 0 var(--sp-ref-spacing-2);
    --sp-comp-icon-size: var(--sp-ref-font-size-small);

    // sp-select has no size for its option list, only these tokens. A page size is one short number,
    // so the rows lose the divider and the height a sentence would need.
    --sp-comp-list-item-content-min-height: 0;
    --sp-comp-list-item-min-height: 0;
    --sp-comp-list-item-padding-block: var(--sp-ref-spacing-2);
    --sp-comp-list-item-padding-inline: var(--sp-ref-spacing-3);
    --sp-comp-list-item-content-font: 1em;
    --sp-comp-list-item-display-divider: none;
    --sp-comp-list-item-active-display-divider: none;
    --sp-comp-list-item-active-hover-display-divider: none;
    --sp-comp-list-item-prepend-min-width: var(--sp-ref-spacing-6);

    width: var(--sp-comp-table-per-page-width, 4.75rem);
    font-size: 1em;
  }
}
</style>
